/**
 * The facts this browser has missed on a quiz recently and not yet answered
 * correctly twice since. A list to look at again — explicitly **not** a mastery
 * model, a fluency score, or a grade estimate (ADR 0012 §1). The data a shared
 * browser with no idea who is typing can collect does not support that claim.
 *
 * Local to the browser, guarded like bestScore.ts and RecentWorksheets.ts:
 * every access wrapped, nothing sent anywhere. Unlike a class roster this is
 * worth keeping — it is arithmetic and timestamps, not personal data.
 */

const STORAGE_KEY = "mathpages.missedFacts";

/** A fact leaves the list after this many correct answers in a row. */
const CLEAR_AFTER = 2;

/** Guard against a corrupted store growing without bound; not a real limit. */
const MAX_FACTS = 100;

interface FactEntry {
    misses: number;
    lastMissed: number;
    correctSince: number;
}

type Store = Record<string, FactEntry>;

/** Normalises a shown problem ("7 x 8") to a fact key ("7x8"). */
export function factKey(front: string): string {
    return front.replace(/\s+/g, "");
}

function readAll(): Store {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (!stored) {
            return {};
        }
        const parsed = JSON.parse(stored);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
            return {};
        }
        const clean: Store = {};
        for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
            if (
                value &&
                typeof value === "object" &&
                typeof (value as FactEntry).misses === "number" &&
                typeof (value as FactEntry).lastMissed === "number" &&
                typeof (value as FactEntry).correctSince === "number"
            ) {
                clean[key] = value as FactEntry;
            }
        }
        return clean;
    } catch {
        return {};
    }
}

function write(store: Store): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch {
        // Full, disabled, or unavailable. Not worth an error over a practice list.
    }
}

export function recordMiss(key: string): void {
    const store = readAll();
    const existing = store[key];
    store[key] = {
        misses: (existing?.misses ?? 0) + 1,
        lastMissed: Date.now(),
        correctSince: 0,
    };

    // Trim the oldest if the store has grown past the guard cap.
    const keys = Object.keys(store);
    if (keys.length > MAX_FACTS) {
        keys
            .sort((a, b) => store[a].lastMissed - store[b].lastMissed)
            .slice(0, keys.length - MAX_FACTS)
            .forEach((old) => delete store[old]);
    }

    write(store);
}

export function recordCorrect(key: string): void {
    const store = readAll();
    const existing = store[key];
    if (!existing) {
        return; // A correct answer on a fact not in the list is a no-op.
    }
    const correctSince = existing.correctSince + 1;
    if (correctSince >= CLEAR_AFTER) {
        delete store[key];
    } else {
        store[key] = { ...existing, correctSince };
    }
    write(store);
}

/** The missed facts, most recently missed first. */
export function readMissedFacts(): string[] {
    return Object.entries(readAll())
        .sort(([, a], [, b]) => b.lastMissed - a.lastMissed)
        .map(([key]) => key);
}

export function clearMissedFacts(): void {
    write({});
}
